import { applyDependencies } from 'components/users/permissions/permission_dependencies';

import type { DependencyRule } from 'components/users/permissions/permission_dependencies';
import type {
  AssignableRole, GraphOnlyPermission, PermissionGroup, PermissionLabel, PermissionRow, PermissionSummary,
} from './types';

export type PermissionValues = Record<string, boolean>;
export type PermissionLabels = Record<string, string>;

// office_admin is a row like any other here; permission_dependencies tracks it separately only because the
// old tab renders it outside the checkbox grid, so it is fed back in from the same map.
const OFFICE_ADMIN = 'office_admin';
const BACKOFFICE_USER = 'backoffice_user';

export const rowsOf = (groups: PermissionGroup[]): PermissionRow[] => (
  groups.reduce<PermissionRow[]>((all, group) => all.concat(group.rows), [])
);

// Serialized as a list of pairs, not a hash, because the props go through camelized_hash on the way here
// and that rewrites hash keys — see UserPermissionViewSerializer#permission_labels.
export const labelsOf = (labels: PermissionLabel[]): PermissionLabels => (
  labels.reduce<PermissionLabels>((map, { key, label }) => ({ ...map, [key]: label }), {})
);

// The rendered rows PLUS the graph-only permissions: the graph has to be re-applied over the same universe
// the server resolves, or a rule whose source has no row here stops applying. Nothing submits that half.
export const valuesOf = (rows: PermissionRow[], graphOnly: GraphOnlyPermission[] = []): PermissionValues => {
  const values: PermissionValues = {};

  graphOnly.forEach((permission) => {
    values[permission.key] = permission.granted;
  });
  rows.forEach((row) => {
    values[row.key] = row.granted;
  });

  return values;
};

// One place, so office_admin is always read from the values being normalized rather than a second copy.
const normalize = (
  rules: DependencyRule[], values: PermissionValues, previous?: PermissionValues,
): PermissionValues => applyDependencies(rules, values, values[OFFICE_ADMIN] === true, previous);

// Rails' Array#to_sentence, which is what the serializer builds these strings with.
export const toSentence = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

// PermissionResolver.resolve with no overrides: union the grants, re-apply the graph.
//
// A feature-sourced row is not derived from the selection: backoffice_user's answer is the backoffice feature
// whatever a role lists, as in PermissionResolver. Its default is its value when editing began (`started`,
// what the server holds), or On once a role picked since then lists it where `rolesEnableBackoffice` says so,
// the same rule as `PermissionWriter#enable_backoffice_for_added_roles`. The page submits that On, which switches
// the feature on. Once switched by hand (`overridden`) it is the hand-set value, because the server switches the
// feature before measuring overrides. Rows only: for a viewer who is not a back office admin, backoffice_user is a
// graph-only value derived from the roles like any other, and nothing submits it.
export function roleDefaults(
  rules: DependencyRule[],
  rows: PermissionRow[],
  roles: AssignableRole[],
  roleIds: number[],
  values: PermissionValues,
  started: { values: PermissionValues; roleIds: number[] } | null = null,
  rolesEnableBackoffice = false,
  overridden: Set<string> = new Set(),
): PermissionValues {
  const start = started ?? { values, roleIds };
  const defaults: PermissionValues = {};
  Object.keys(values).forEach((key) => {
    defaults[key] = false;
  });

  roles.filter(role => roleIds.includes(role.id)).forEach((role) => {
    role.grants.forEach((key) => {
      if (key in defaults) defaults[key] = true;
    });
  });

  const switchesBackofficeOn = rolesEnableBackoffice && roles.some(role => (
    roleIds.includes(role.id) && !start.roleIds.includes(role.id) && role.grants.includes(BACKOFFICE_USER)
  ));

  rows.filter(row => row.featureSourced).forEach((row) => {
    defaults[row.key] = overridden.has(row.key)
      ? values[row.key]
      : start.values[row.key] || (row.key === BACKOFFICE_USER && switchesBackofficeOn);
  });

  return normalize(rules, defaults);
}

// The two-part question UserPermissionViewSerializer#custom? asks: an override exists AND it changes the
// answer. Without the second half an override agreeing with the roles reads as customised; without the first,
// so does a row pushed off its default by ANOTHER permission's override — overriding can_send_emails off
// drags every email permission with it through the graph, and none of those is a change to that row.
export function isCustom(
  row: PermissionRow, values: PermissionValues, defaults: PermissionValues, overridden: Set<string>,
): boolean {
  if (row.featureSourced) return false;
  if (values[row.key] === defaults[row.key]) return false;

  // A locked row cannot be toggled, so it can only sit off its role default because withRoles pinned it while
  // a new role moved that default underneath. The save turns that pin into a real override, so it IS custom.
  return overridden.has(row.key) || row.locked;
}

// `previous` is what makes the release cascade work: revoking voucher access has to release the deal access
// it had forced on, which is a transition rather than a steady state.
export function withValue(
  rules: DependencyRule[], values: PermissionValues, key: string, value: boolean,
): PermissionValues {
  return normalize(rules, { ...values, [key]: value }, values);
}

// Overrides are deltas and survive a role change, so a permission carrying one keeps its value and
// everything else picks up the new selection. Locked rows hold their value too, so the save records a
// compensating override rather than being rejected.
export function withRoles(
  rules: DependencyRule[],
  rows: PermissionRow[],
  values: PermissionValues,
  previousDefaults: PermissionValues,
  nextDefaults: PermissionValues,
  overridden: Set<string>,
): PermissionValues {
  const rowsByKey = new Map(rows.map(row => [row.key, row]));
  const next: PermissionValues = {};

  Object.keys(values).forEach((key) => {
    const row = rowsByKey.get(key);
    // A graph-only permission has no row to carry an override flag, so departing from the roles is the only
    // evidence there is one.
    const carried = row ? overridden.has(key) : values[key] !== previousDefaults[key];

    next[key] = row?.locked || carried ? values[key] : nextDefaults[key];
  });

  return normalize(rules, next, values);
}

// Mirrors UserPermissionViewSerializer#blocking_requirements.
export function blockedBy(
  rules: DependencyRule[], values: PermissionValues, labels: PermissionLabels, key: string,
): string[] {
  if (values[key]) return [];

  const rule = rules.find(candidate => candidate.attr === key);

  return (rule?.requires ?? []).filter(requirement => !values[requirement])
    .map(requirement => labels[requirement] ?? requirement);
}

// The other two rule shapes. `requires` has blockedBy; without these a switch went dead or froze on with no
// explanation. forcedOnBy first, matching the order applyDependencies resolves them in.
export function dependencyNote(
  rules: DependencyRule[], values: PermissionValues, labels: PermissionLabels, key: string,
): string | null {
  const rule = rules.find(candidate => candidate.attr === key);
  if (!rule) return null;

  const nameOf = (source: string) => labels[source] ?? source;

  const forcing = (rule.forcedOnBy ?? []).filter(source => values[source]);
  if (forcing.length > 0) return `Turned on by ${toSentence(forcing.map(nameOf))}.`;

  const superseding = (rule.lockedBy ?? []).filter(source => values[source]);
  if (superseding.length > 0) return `Already covered by ${toSentence(superseding.map(nameOf))}.`;

  return null;
}

// Mirrors UserPermissionViewSerializer#attribution, recomputed rather than carried over: a line still
// reading "From Broker" after Broker was removed is worse than none. Reads the roles' RAW grants, as the
// serializer does — a role that grants something the graph then blocks still granted it.
export function attribution(
  row: PermissionRow, roles: AssignableRole[], roleIds: number[], values: PermissionValues,
): string | null {
  const granted = values[row.key];

  if (row.featureSourced) return granted ? "From this account's features, not from a role" : null;

  const granting = roles.filter(role => roleIds.includes(role.id) && role.grants.includes(row.key));
  if (granting.length === 0) return granted ? 'Not part of any assigned role' : null;

  return `From ${toSentence(granting.map(role => role.name))}`;
}

// Rendered rows only, so it agrees with what is on screen beneath it.
export function summarize(
  rows: PermissionRow[], values: PermissionValues, defaults: PermissionValues, overridden: Set<string>,
): PermissionSummary {
  return {
    granted: rows.filter(row => values[row.key]).length,
    total: rows.length,
    customised: rows.filter(row => isCustom(row, values, defaults, overridden)).length,
  };
}
